import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserSplits, deleteSplit } from '../services/api';
import { Split } from '../types';
import { useToast } from './Toast';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import DataTable, { Column, FilterConfig } from './DataTable';
import { PrimaryButton, SecondaryButton, DangerButton } from './buttons';
import { getInitials } from '../utils/stringUtils';

interface WorkWithAccount extends Split {
  accountName?: string;
  accountId?: string;
}

const ManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { accounts } = useAccount();
  const { user } = useAuth();
  const [works, setWorks] = useState<WorkWithAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingWork, setDeletingWork] = useState<WorkWithAccount | null>(null);
  const { showToast, ToastComponent } = useToast();

  useEffect(() => {
    loadWorks();
  }, []);

  const loadWorks = async () => {
    try {
      setLoading(true);
      const data = await getUserSplits();
      // In the future, the API will return works with account info
      // For now, we just display all works
      setWorks(data as WorkWithAccount[]);
      setError(null);
    } catch (err) {
      setError('Failed to load works');
      console.error('Error loading works:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingWork) return;

    try {
      await deleteSplit(deletingWork.id);
      setWorks(works.filter(work => work.id !== deletingWork.id));
      setDeletingWork(null);
      showToast('Work deleted successfully', 'success');
    } catch (err) {
      showToast('Failed to delete work', 'error');
      console.error('Error deleting work:', err);
    }
  };

  const getStatusBadge = (status: Split['status']) => {
    const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold';
    
    switch (status) {
      case 'confirmed':
      case 'signed':
        return `${baseClasses} bg-[#10B98133] text-[#10B981]`;
      case 'pending':
      case 'unsigned':
        return `${baseClasses} bg-[#F59E0B33] text-[#F59E0B]`;
      case 'draft':
      default:
        return `${baseClasses} bg-[#9CA3AF33] text-[#9CA3AF]`;
    }
  };

  const getStatusText = (work: Split) => {
    switch (work.status) {
      case 'confirmed':
      case 'signed':
        return 'Confirmed';
      case 'pending':
      case 'unsigned':
        return 'Pending';
      case 'draft':
      default:
        return 'Draft';
    }
  };

  // Group works by status for the overview cards
  const worksByStatus = {
    needsConfirmation: works.filter(w => w.status === 'unsigned' || w.status === 'pending'),
    confirmed: works.filter(w => w.status === 'signed' || w.status === 'confirmed'),
    draft: works.filter(w => w.status === 'draft'),
  };

  // Define table columns
  const columns: Column<WorkWithAccount>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (work) => (
        <div className="font-semibold text-white">{work.title}</div>
      ),
    },
    {
      key: 'accountName',
      header: 'Artist Account',
      sortable: true,
      render: (work) => (
        <div className="text-[#9CA3AF]">
          {work.accountName ? (
            <span className="inline-flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#14B8A6] flex items-center justify-center text-sm text-white font-medium">
                {getInitials(work.accountName)}
              </span>
              {work.accountName}
            </span>
          ) : (
            <span className="text-[#9CA3AF] italic">Unassigned</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (work) => (
        <span className={getStatusBadge(work.status)}>
          {getStatusText(work)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (work) => (
        <div className="text-[#9CA3AF] text-sm">
          {new Date(work.createdAt).toLocaleDateString()}
        </div>
      ),
    },
  ];

  // Define filters
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'All Statuses',
      type: 'select',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'unsigned', label: 'Pending' },
        { value: 'signed', label: 'Confirmed' },
      ],
    },
    {
      key: 'accountName',
      label: 'All Artists',
      type: 'select',
      options: accounts
        .filter(a => a.type === 'artist')
        .map(account => ({ value: account.name, label: account.name })),
    },
  ];

  // Render actions for each row
  const renderActions = (work: WorkWithAccount) => (
    <>
      <button
        onClick={() => setDeletingWork(work)}
        className="p-2 text-content-muted hover:text-state-error hover:bg-red-500/10 rounded-lg transition-colors"
        title="Delete work"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </>
  );

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        {ToastComponent}
        <div className="card border-red-500/40 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-300">Error</h3>
              <div className="mt-2 text-sm text-red-200">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={loadWorks}
                  className="bg-red-100 px-2 py-1 text-sm rounded text-red-800 hover:bg-red-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#18181B]">
      <div className="container mx-auto px-4 py-8">
        {ToastComponent}
        
        {/* Header Section */}
        <div className="bg-surface-secondary border border-border-subtle rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Roster Overview</h1>
              <p className="text-sm sm:text-base text-[#6B7280]">
                {user?.name && <span className="text-[#14B8A6] font-medium">{user.name}</span>}
                {user?.name && ' • '}All artists and works
              </p>
            </div>
            <Link to="/create-split" className="w-full sm:w-auto">
              <PrimaryButton className="w-full sm:w-auto">Create New Split</PrimaryButton>
            </Link>
          </div>
        </div>

        {/* Stats Overview - hidden on mobile */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Artists Card */}
          <div className="relative bg-[#27272A] border border-[#3F3F46] rounded-2xl p-5 overflow-hidden shadow-card">
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-[radial-gradient(circle,_#A855F720_0%,_transparent_70%)] blur-xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#14B8A6] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#9CA3AF]">Artists</p>
                </div>
                <p className="text-3xl font-bold text-white">
                  {accounts.filter(a => a.type === 'artist').length}
                </p>
              </div>
            </div>
          </div>

          {/* Catalog Card */}
          <div className="relative bg-[#27272A] border border-[#3F3F46] rounded-2xl p-5 overflow-hidden shadow-card">
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-[radial-gradient(circle,_#14B8A620_0%,_transparent_70%)] blur-xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#14B8A633] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#14B8A6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#9CA3AF]">Catalog</p>
                </div>
                <p className="text-3xl font-bold text-white">{works.length}</p>
              </div>
            </div>
          </div>

          {/* Pending Confirmations Card */}
          <div className="relative bg-[#27272A] border border-[#3F3F46] rounded-2xl p-5 overflow-hidden shadow-card">
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-[radial-gradient(circle,_#A855F720_0%,_transparent_70%)] blur-xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#A855F733] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#A855F7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#9CA3AF]">Pending</p>
                </div>
                <p className="text-3xl font-bold text-white">{worksByStatus.needsConfirmation.length}</p>
              </div>
            </div>
          </div>

          {/* Confirmed Card */}
          <div className="relative bg-[#27272A] border border-[#3F3F46] rounded-2xl p-5 overflow-hidden shadow-card">
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-[radial-gradient(circle,_#10B98120_0%,_transparent_70%)] blur-xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#10B98133] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#9CA3AF]">Confirmed</p>
                </div>
                <p className="text-3xl font-bold text-white">{worksByStatus.confirmed.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* All Works Table */}
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl overflow-hidden shadow-card">
          <div className="p-6 border-b border-[#3F3F46]">
            <h2 className="text-xl font-bold text-white">All Works</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#14B8A6]"></div>
              <p className="mt-4 text-[#9CA3AF]">Loading works...</p>
            </div>
          ) : works.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#14B8A633] flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#14B8A6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No works yet</h3>
              <p className="text-[#9CA3AF] mb-4">
                Create your first split to get started
              </p>
              <Link to="/create-split" className="btn-primary inline-flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New Split
              </Link>
            </div>
          ) : (
            <DataTable
              data={works}
              columns={columns}
              filters={filters}
              searchPlaceholder="Search works..."
              searchKeys={['title']}
              renderActions={renderActions}
              onRowClick={(work) => navigate(`/splits/${work.id}`)}
            />
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deletingWork && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-secondary rounded-xl shadow-xl p-6 max-w-md w-full border border-border-subtle">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-content">Delete Work?</h3>
              <button
                onClick={() => setDeletingWork(null)}
                className="text-content-muted hover:text-content transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <h4 className="text-content font-medium mb-2">Work Details:</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-content-secondary">
                    <svg className="w-4 h-4 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <strong className="mr-2">Title:</strong> {deletingWork.title}
                  </div>
                  {deletingWork.accountName && (
                    <div className="flex items-center text-content-secondary">
                      <svg className="w-4 h-4 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <strong className="mr-2">Artist:</strong> {deletingWork.accountName}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <SecondaryButton
                onClick={() => setDeletingWork(null)}
                className="flex-1"
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleDelete}
                className="flex-1"
              >
                Delete Work
              </DangerButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerDashboard;
